import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";

export const scrollToAnchor = (anchor) => {
  const el = document.getElementById(anchor);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (anchor) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToAnchor(anchor), 350);
    } else {
      scrollToAnchor(anchor);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2 bg-[#0a0b1a]/85 backdrop-blur-xl border-b border-white/[0.06]" : "py-2.5 bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          aria-label="Les Amis du Web — Accueil"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              scrollToAnchor("accueil");
            }
          }}
          className="flex items-center flex-shrink-0"
          data-testid="header-logo"
        >
          <img
            src="/logo-amisduweb.png"
            alt="Les Amis du Web — création de sites internet sur mesure"
            className={`h-auto transition-all duration-500 ${
              scrolled ? "w-[105px] md:w-[118px] xl:w-[132px]" : "w-[110px] md:w-[130px] xl:w-[150px]"
            }`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="header-nav">
          {NAV_LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                className="link-underline text-[15px] xl:text-[15.5px] font-normal text-[var(--text-secondary)] hover:text-white transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.anchor}
                href={l.anchor === "accueil" ? "/" : `/#${l.anchor}`}
                onClick={(e) => {
                  e.preventDefault();
                  goTo(l.anchor);
                }}
                className="link-underline text-[15px] xl:text-[15.5px] font-normal text-[var(--text-secondary)] hover:text-white transition-colors"
                data-testid={`nav-link-${l.anchor}`}
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/demande"
            className="btn-gradient hidden sm:inline-flex items-center gap-2 rounded-full px-[22px] py-3 text-sm font-semibold text-white"
            data-testid="header-cta"
          >
            Faire une demande
            <ArrowRight size={15} />
          </Link>
          <button
            className="lg:hidden glass rounded-full p-2.5 text-white"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden mx-4 mt-3 rounded-2xl bg-[#0d0e20]/95 backdrop-blur-2xl border border-white/[0.08] p-4"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block w-full text-left px-4 py-3 rounded-xl text-[var(--text-secondary)] hover:text-white hover:bg-white/[0.04] transition-colors"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.anchor}
                  href={l.anchor === "accueil" ? "/" : `/#${l.anchor}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goTo(l.anchor);
                  }}
                  className="block w-full text-left px-4 py-3 rounded-xl text-[var(--text-secondary)] hover:text-white hover:bg-white/[0.04] transition-colors"
                  data-testid={`mobile-nav-${l.anchor}`}
                >
                  {l.label}
                </a>
              )
            )}
            <Link
              to="/demande"
              onClick={() => setOpen(false)}
              className="btn-gradient mt-3 flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white"
              data-testid="mobile-menu-cta"
            >
              Faire une demande <ArrowRight size={15} />
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
